import type { PayloadAction } from '@reduxjs/toolkit';
import { createReducer } from '@reduxjs/toolkit';
import {
  initAgentSessionFailure,
  initAgentSessionRequest,
  initAgentSessionSuccess,
  sendMessageAgentSessionRequest,
  sendMessageAgentSessionSuccess,
  setInitialResponse,
  updateAgentSession,
} from 'actions/agentSessions';

import { AgentMessage, ChatAgentSession, ErrorObject } from 'types';
import { AppendEventResponseData } from 'utils/socket';

type EntityState = {
  loading: string[];
  error: ErrorObject | null;
  activeSessionId: string | null;
  revision: number;
  status: string;
  state: ChatAgentSession | null;
  sessionMessages: AgentMessage[];
};

const defaultState: EntityState = {
  loading: [],
  error: null,
  activeSessionId: null,
  revision: 0,
  state: null,
  status: '',
  sessionMessages: [],
};

type AgentSessionsReducer<P = void> = (
  state: EntityState,
  payload: PayloadAction<P>
) => EntityState;

const handleInitAgentSessionRequest: AgentSessionsReducer = (state) => ({
  ...state,
  loading: [...state.loading, initAgentSessionRequest.toString()],
});

const handleInitAgentSessionSuccess: AgentSessionsReducer<
  ReturnType<typeof initAgentSessionSuccess>['payload']
> = (state, { payload }) => ({
  ...state,
  sessionMessages: [],
  activeSessionId: payload.sessionId,
  loading: state.loading.filter((s) => s !== initAgentSessionRequest.toString()),
});

const handleInitAgentSessionFailure: AgentSessionsReducer<ErrorObject> = (state, { payload }) => ({
  ...state,
  error: payload,
  loading: state.loading.filter((s) => s !== initAgentSessionRequest.toString()),
});

const handleSetInitialResponse: AgentSessionsReducer<ChatAgentSession> = (state, { payload }) => ({
  ...state,
  revision: payload.revision,
  state: payload,
  sessionMessages: payload.messages,
  status: payload.status,
});

const handleUpdateAgentSession: AgentSessionsReducer<AppendEventResponseData> = (
  state,
  { payload }
) => ({
  ...state,
  revision: payload.revision,
  state: payload.state,
  sessionMessages: payload.state?.messages || [],
  status: payload.status,
});

const handleSendMessageAgentSessionRequest: AgentSessionsReducer<
  ReturnType<typeof sendMessageAgentSessionRequest>['payload']
> = (state, { payload }) => ({
  ...state,
  sessionMessages: [
    ...state.sessionMessages,
    { content: payload.message, metadata: {}, role: 'user', type: '' },
  ],
});

const handleSendMessageAgentSessionSuccess: AgentSessionsReducer<
  ReturnType<typeof sendMessageAgentSessionSuccess>['payload']
> = (state, { payload }) => ({
  ...state,
  sessionMessages: [...state.sessionMessages, payload.message],
});

const handlers = {
  [initAgentSessionRequest.toString()]: handleInitAgentSessionRequest,
  [initAgentSessionSuccess.toString()]: handleInitAgentSessionSuccess,
  [initAgentSessionFailure.toString()]: handleInitAgentSessionFailure,

  [setInitialResponse.toString()]: handleSetInitialResponse,

  [updateAgentSession.toString()]: handleUpdateAgentSession,

  [sendMessageAgentSessionRequest.toString()]: handleSendMessageAgentSessionRequest,
  [sendMessageAgentSessionSuccess.toString()]: handleSendMessageAgentSessionSuccess,
};

const entitiesReducer = createReducer(defaultState, handlers);

export default entitiesReducer;
